import { Injectable } from "@angular/core";
import { BehaviorSubject, from } from "rxjs";
// Layout
import { LayoutConfigModel } from "../../interfaces/home/layout-config";

export interface ClassType {
  header: string[];
  header_mobile: string[];
  header_menu: string[];
  aside_menu: string[];
  aside: string[];
}

@Injectable({
  providedIn: "root"
})
export class HtmlClassService {
  // Public properties
  config: LayoutConfigModel;
  classes: ClassType;
  onClassesUpdated$: BehaviorSubject<ClassType>;
  // Private properties
  private loaded: string[] = [];

  /**
   * Component constructor
   */
  constructor() {
    this.onClassesUpdated$ = new BehaviorSubject(this.classes);
  }

  /**
   * Build html element classes from layout config
   * @param layoutConfig
   */
  setConfig(layoutConfig: LayoutConfigModel) {
    this.config = layoutConfig;

    // scope list of classes
    this.classes = {
      header: [],
      header_mobile: [],
      header_menu: [],
      aside_menu: [],
      aside: []
    };

    // init base layout
    this.initLayout();
    this.initLoader();

    // init header and subheader menu
    this.initHeader();
    this.initSubheader();

    // init aside and aside menu
    this.initAside();

    // init footer
    this.initFooter();

    this.onClassesUpdated$.next(this.classes);
  }

  /**
   * Returns Classes
   *
   * @param path: string
   * @param toString boolean
   */
  getClasses(path?: string, toString?: boolean): ClassType | string[] | string {
    if (path) {
      const classes = this.classes[path] || "";
      if (toString && Array.isArray(classes)) {
        return classes.join(" ");
      }
      return classes.toString();
    }
    return this.classes;
  }

  /**
   * Init Layout
   */
  private initLayout() {
    if (this.config.self.body.class) {
      const _selfBodyClass = this.config.self.body.class.toString();
      if (_selfBodyClass) {
        const bodyClasses: string[] = _selfBodyClass.split(" ");
        bodyClasses.forEach(cssClass => document.body.classList.add(cssClass));
      }
    }

    if (
      this.config.self.layout === "boxed" &&
      this.config.self.body["background-image"]
    ) {
      document.body.style.backgroundImage =
        'url("' + this.config.self.body["background-image"] + '")';
    }

    // Offcanvas directions
    document.body.classList.add("kt-quick-panel--right");
    document.body.classList.add("kt-demo-panel--right");
    document.body.classList.add("kt-offcanvas-panel--right");
  }

  /**
   * Init Loader
   */
  private initLoader() {}

  /**
   * Init Header
   */
  private initHeader() {
    // Fixed header
    if (this.config.header.self.fixed.desktop) {
      document.body.classList.add("kt-header--fixed");

      this.classes.header.push("kt-header--fixed");
    } else {
      document.body.classList.add("kt-header--static");
    }

    if (this.config.header.self.fixed.mobile) {
      document.body.classList.add("kt-header-mobile--fixed");
      this.classes.header_mobile.push("kt-header-mobile--fixed");
    }

    if (this.config.header.menu.self.layout) {
      this.classes.header_menu.push(
        "kt-header-menu--layout-" + this.config.header.menu.self.layout
      );
    }
  }

  /**
   * Inin Subheader
   */
  private initSubheader() {
    // Fixed content head
    if (this.config.subheader.fixed) {
      document.body.classList.add("kt-subheader--fixed");
    }

    if (this.config.subheader.display) {
      document.body.classList.add("kt-subheader--enabled");
    }

    if (this.config.subheader.style) {
      document.body.classList.add(
        "kt-subheader--" + this.config.subheader.style
      );
    }
  }

  /**
   * Init Aside
   */
  private initAside() {
    if (this.config.aside.self.display !== true) {
      return;
    }

    document.body.classList.add("kt-aside--enabled");

    // Fixed Aside
    if (this.config.aside.self.fixed) {
      document.body.classList.add("kt-aside--fixed");
      this.classes.aside.push("kt-aside--fixed");
    } else {
      document.body.classList.add("kt-aside--static");
    }

    // Default fixed
    if (this.config.aside.self.minimize.default) {
      document.body.classList.add("kt-aside--minimize");
    }

    // Menu
    // Dropdown Submenu
    if (
      this.config.aside.self.fixed !== true &&
      this.config.aside.menu.dropdown
    ) {
      this.classes.aside_menu.push("kt-aside-menu--dropdown");
      // enable menu dropdown
    }
  }

  /**
   * Init Footer
   */
  private initFooter() {
    // Fixed header
    if (this.config.footer.self.fixed) {
      document.body.classList.add("kt-footer--fixed");
    }
  }
}
